// src/templates/requests/ProviderAnalytics.jsx
import React, { useEffect, useState } from 'react';
import { requestsService } from '../../services/requestsService';

/**
 * ProviderAnalytics
 * Props:
 *  - bookId
 *  - providerId (optional) : when supplied, compute provider-specific KPIs
 */
const ProviderAnalytics = ({ bookId, providerId = null }) => {
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState({
    totalRequests: 0,
    totalOffers: 0,
    totalProviders: 0,
    acceptedOffers: 0,
    acceptanceRate: 0,
    avgResponseTimeHours: null
  });

  useEffect(() => {
    load();
  }, [bookId, providerId]);

  const load = async () => {
    setLoading(true);
    try {
      // Base stats
      const stats = await requestsService.getStats(bookId);
      // get all offers (owner-level) or provider only
      let offers = [];
      if (providerId) {
        offers = await requestsService.getOffersForProvider?.(bookId, providerId) || [];
      } else {
        offers = await requestsService.getOffersForRequest?.(bookId) || [];
      }

      const totalOffers = offers.length;
      const acceptedOffers = offers.filter(o => (o.status || '').toUpperCase() === 'ACCEPTED').length;
      const acceptanceRate = totalOffers === 0 ? 0 : Math.round((acceptedOffers / totalOffers) * 100);

      // avg response time: if offer.createdAt and request.createdAt present compute delta
      let totalResponseHours = 0;
      let responseCount = 0;
      for (const o of offers) {
        if (o.createdAt && o.requestCreatedAt) {
          const a = new Date(o.createdAt).getTime();
          const b = new Date(o.requestCreatedAt).getTime();
          if (!isNaN(a) && !isNaN(b) && a >= b) {
            totalResponseHours += (a - b) / (1000 * 60 * 60);
            responseCount++;
          }
        }
      }
      const avgResponseTimeHours = responseCount === 0 ? null : (totalResponseHours / responseCount).toFixed(1);

      setKpi({
        totalRequests: stats.totalRequests ?? 0,
        totalOffers,
        totalProviders: stats.totalProviders ?? 0,
        acceptedOffers,
        acceptanceRate,
        avgResponseTimeHours
      });
    } catch (e) {
      console.error('Failed to load analytics', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="py-8 text-center">Loading analytics…</div>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Stat label="Total Requests" value={kpi.totalRequests} />
      <Stat label="Total Offers" value={kpi.totalOffers} />
      <Stat label="Providers" value={kpi.totalProviders} />

      <Stat label="Accepted Offers" value={kpi.acceptedOffers} />
      <Stat label="Acceptance Rate" value={`${kpi.acceptanceRate}%`} />
      <Stat label="Avg Response (hrs)" value={kpi.avgResponseTimeHours ?? '—'} />
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="p-4 bg-white dark:bg-slate-800 rounded-xl border">
    <div className="text-sm text-slate-500">{label}</div>
    <div className="text-2xl font-bold mt-2 text-slate-900 dark:text-white">{value}</div>
  </div>
);

export default ProviderAnalytics;
